import {Button, DotProgress} from '@wandb/components';
import copyToClipboard from 'copy-to-clipboard';
import {isEqual} from 'lodash';
import React, {memo} from 'react';

import {DD_PRIVACY_HIDDEN} from '../../../util/datadog/privacy';
import {FileBrowserLayout} from '../FileBrowserLayout';
import {fileInfoFromName} from '../fileInfoFromName';
import {FilePath} from '../FilePath';
import {formatDownloadUrl} from '../formatDownloadUrl';
import type {
  FileBrowserFile,
  UseFileBrowserLoadFile,
  UseFileBrowserLoadFileUrl,
} from '../types';
import {useFile} from '../useFile';
import {FilePreview} from './FilePreview';

interface FileViewProps {
  artifactId: string | null;
  file: FileBrowserFile;
  filePath: string[];
  onDownload(file: FileBrowserFile): void;
  useLoadFile: UseFileBrowserLoadFile;
  useLoadFileUrl: UseFileBrowserLoadFileUrl;
}

const FileViewComponent = function FileView({
  artifactId,
  file,
  filePath,
  onDownload,
  useLoadFile,
  useLoadFileUrl,
}: FileViewProps) {
  const fileInfo = fileInfoFromName(file.name);
  const {
    data: fileBody,
    loading,
    error,
  } = useFile(useLoadFile, file, {skip: !fileInfo.copyable});

  const topRight = (
    <>
      {fileInfo.copyable ? (
        <Button
          startIcon="copy"
          variant="secondary"
          isDisabled={loading || Boolean(error) || !fileBody.length}
          onPress={() => copyToClipboard(fileBody)}>
          Copy file contents
        </Button>
      ) : null}
      <Button
        data-dd-privacy={DD_PRIVACY_HIDDEN}
        startIcon="download"
        variant="secondary"
        href={formatDownloadUrl(file.url!, file.name)}
        onPress={() => onDownload(file)}
        download={file.name}>
        Download
      </Button>
    </>
  );

  if (loading) {
    return (
      <FileBrowserLayout
        topLeft={<FilePath currentFile={file} filePath={filePath} />}>
        <div className="flex items-center p-16 justify-center border-wb-border rounded-lg border min-h-200">
          <DotProgress aria-label="Loading file..." size="lg" />
        </div>
      </FileBrowserLayout>
    );
  }
  if (error) {
    return <FileBrowserLayout>{error}</FileBrowserLayout>;
  }

  return (
    <FileBrowserLayout
      topLeft={<FilePath currentFile={file} filePath={filePath} />}
      topRight={topRight}>
      <FilePreview
        artifactId={artifactId}
        useLoadFile={useLoadFile}
        useLoadFileUrl={useLoadFileUrl}
        file={file}
        fileBody={fileBody}
        onDownload={onDownload}
      />
    </FileBrowserLayout>
  );
};

// Rerendering this component can have the effect of refetching the jupyter
// notebook et. al. over and over again if history key polling is enabled.
export const FileView = memo(FileViewComponent, isEqual);
